import React, { useEffect, useRef } from 'react';
import { generateClient } from '@aws-amplify/api';
import { onLangflowResponse } from '@alucio/aws-beacon-amplify/src/graphql/subscriptions';
import { OnLangflowResponseSubscription } from '@alucio/aws-beacon-amplify/src/API';
import { AIScientificSummary, useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';
import loggers from 'src/utils/logger';
import { useCurrentUser } from '../../../../state/redux/selector/user';
import {
  LangflowResponse,
  LangflowSubscription,
  parseAISearchResponse,
  useAIReferences,
  collectAISearchReferences,
} from './useAISearchHooks';

import { buildAISearchHistoryEntry, recordAISearchHistory } from 'src/utils/aiSearchHistory';

const AI_SEARCH_TIMEOUT = 120000; // 2 minutes

const trackAISearchCompletionAnalytics = (
  totalDuration: number,
  searchText: string,
) => {
  try {
    analytics?.track('AI_SEARCH_REQUEST', {
      category: 'SEARCH',
      action: 'REQUEST',
      totalTime: totalDuration,
      searchText,
    });
  } catch (error) {
    loggers.search.error('Failed to track AI search analytics:', error);
  }
};

const AISearchManager: React.FC = () => {
  const { aiSearchSessionData, dispatchAiSearch, allDocVersions } = useDocumentSearchV2Context();
  const { sessionId, loading, searchText, request } = aiSearchSessionData;

  const currentUser = useCurrentUser();
  const userId = currentUser.userProfile?.id;

  const { fetchReferences } = useAIReferences();
  const documentsRef = useRef(allDocVersions);
  documentsRef.current = allDocVersions;
  const subscriptionRef = useRef<LangflowSubscription | null>(null);
  const sessionStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!userId || !sessionId || !loading) return;

    loggers.search.info('Establishing AI Search subscription for session:', sessionId);
    sessionStartedAtRef.current = performance.now();

    const finalizeSearch = (report: AIScientificSummary | null = null, error: string | null = null) => {
      if (sessionStartedAtRef.current === null) {
        return;
      }

      const totalDuration = sessionStartedAtRef.current === null
        ? 0
        : (performance.now() - sessionStartedAtRef.current) / 1000;
      sessionStartedAtRef.current = null;
      if (request) {
        recordAISearchHistory(buildAISearchHistoryEntry(
          request, sessionId, searchText, report ? 'success' : 'error', report,
          report ? collectAISearchReferences(report, documentsRef.current) : [], error,
        ));
      }
      trackAISearchCompletionAnalytics(totalDuration, searchText);
    };

    const cleanupSearchSession = () => {
      clearTimeout(timeoutId);
      if (subscriptionRef.current) {
        loggers.search.info('Unsubscribing from AI Search session:', sessionId);
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };

    const timeoutId = setTimeout(() => {
      loggers.search.warn('AI Search timed out after 2 minutes for session:', sessionId);
      cleanupSearchSession();
      finalizeSearch(null, 'AI Search timed out. Please try again.');
      dispatchAiSearch({
        type: 'SET_ERROR',
        payload: 'AI Search timed out. Please try again.',
      });
    }, AI_SEARCH_TIMEOUT);

    const client = generateClient();
    const sub = client.graphql({
      query: onLangflowResponse,
      variables: {
        userId,
        sessionId,
      },
    }).subscribe({
      next: (msg: { data: OnLangflowResponseSubscription }) => {
        const response = msg.data?.onLangflowResponse as LangflowResponse;
        if (!response) return;
        if (response.sessionId !== sessionId) {
          loggers.search.warn('Ignoring AI Search response for stale session:', response.sessionId);
          return;
        }

        if (response.status === 'ERROR') {
          cleanupSearchSession();
          finalizeSearch(null, response.statusDetail || 'An error occurred during AI processing');
          dispatchAiSearch({
            type: 'SET_ERROR',
            payload: response.statusDetail || 'An error occurred during AI processing',
          });
          return;
        }

        if (response.status === 'INFO') {
          if (response.statusDetail) {
            dispatchAiSearch({ type: 'UPDATE_STATUS', payload: response.statusDetail });
          }
        }

        if (response.status === 'SUCCESS') {
          cleanupSearchSession();
          const parsed = parseAISearchResponse(response);
          finalizeSearch(parsed, parsed ? null : 'Error processing AI Search results.');
          if (parsed) {
            dispatchAiSearch({ type: 'SET_REPORT', payload: parsed });
            fetchReferences(parsed);
          } else {
            loggers.search.error('Error parsing final response:', {
              sessionId: response.sessionId,
              status: response.status,
              statusDetail: response.statusDetail,
            });
            dispatchAiSearch({
              type: 'SET_ERROR',
              payload: 'Error processing AI Search results.',
            });
          }
        }
      },
      error: (err: unknown) => {
        cleanupSearchSession();
        finalizeSearch(null, 'An error occurred while connecting to AI search. Please try again.');
        loggers.search.error('Subscription error:', err);
        // If socket fails, surface a terminal error so the AI search lifecycle ends deterministically
        dispatchAiSearch({
          type: 'SET_ERROR',
          payload: 'An error occurred while connecting to AI search. Please try again.',
        });
      },
    });

    subscriptionRef.current = sub;

    return () => {
      cleanupSearchSession();
    };
  }, [userId, sessionId, loading, dispatchAiSearch, fetchReferences, searchText, request]);

  return null;
};

export default AISearchManager;
